import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { ChangeLine, LocalSwitch, Pill, SectionHeader, type PillKind } from './primitives';
import { RCM_SETTINGS_CHANGE, type RcmSettingsSlug } from './settingsData';
import { NumberSetting, SettingsChangeFooter, useCanEditSettings } from './settingsEdit';
import {
  COUNTED_FOOTER_ID,
  COUNTED_MAX,
  COUNTED_MIN,
  COUNTED_TASKS,
  resetCountedMinutes,
  setCountedMinutes,
  useCountedMinutes,
} from './howCountedStore';
import { NetworksSection, NetworksSummaryLink } from './NetworksSection';
import { Dialog, PhoneField, TextField, formatPhone } from './settingsInteractions';
import { FilterBar, NumberedPager, ListEmpty, useListUrlState, applySearch, paginate } from '@/components/lists';
import { PMS } from '@/features/shared/pms';
import { PAYER_RULES, PAYER_RULE_PAYER_COUNT, findPayer, payerPathFor, rulesForPayer } from './payerRulesMock';

const CH = RCM_SETTINGS_CHANGE;

// ── small helpers ──

function Row({
  title,
  sub,
  help,
  helpMono,
  right,
  children,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  help?: React.ReactNode;
  helpMono?: boolean;
  right?: React.ReactNode;
  /** Full-width content below the row, e.g. an inline edit form. */
  children?: React.ReactNode;
}) {
  return (
    <div className="setd-row" style={children ? { flexWrap: 'wrap' } : undefined}>
      <div style={{ minWidth: 0 }}>
        <div className="setd-row-title">
          {title}
          {sub ? <span className="sub"> {sub}</span> : null}
        </div>
        {help ? <div className={`setd-row-help${helpMono ? ' mono' : ''}`}>{help}</div> : null}
      </div>
      {right ? <div className="setd-row-right">{right}</div> : null}
      {children ? <div style={{ flexBasis: '100%' }}>{children}</div> : null}
    </div>
  );
}

function Select({ value, options, label }: { value: string; options: string[]; label: string }) {
  return (
    <select className="setd-select" defaultValue={value} aria-label={label}>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

const TIMES = [
  '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM',
  '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '8:00 PM', '9:00 PM',
];

// ── rcm-07 Payer portals ──

interface Portal {
  name: string;
  payers: string;
  usedFor: string;
  login: string;
  status: { kind: PillKind; label: string };
  last: string;
  action: string;
}

const PORTALS: Portal[] = [
  { name: 'Availity', payers: 'Aetna, BlueCross BlueShield of Texas, Humana', usedFor: 'Benefits · Claim status · Remittances', login: 'billing@alamo…', status: { kind: 'ok', label: 'Working' }, last: 'Today, 9:42 AM', action: 'Update login' },
  { name: 'Delta Dental · Provider Tools', payers: 'Delta Dental', usedFor: 'Benefits · Claim status · Remittances · Appeals', login: 'alamoheights', status: { kind: 'ok', label: 'Working' }, last: 'Today, 8:10 AM', action: 'Update login' },
  { name: 'Cigna for Health Care Professionals', payers: 'Cigna', usedFor: 'Benefits · Claim status', login: 'ahd_billing', status: { kind: 'ok', label: 'Working' }, last: 'Yesterday, 4:50 PM', action: 'Update login' },
  { name: 'UnitedHealthcare Provider', payers: 'UnitedHealthcare', usedFor: 'Claim status · Remittances', login: 'ahd.billing', status: { kind: 'ok', label: 'Working' }, last: 'Oct 13, 2:10 PM', action: 'Update login' },
  { name: 'Sun Life', payers: 'Sun Life', usedFor: 'Benefits · Claim status', login: 'alamo_ins', status: { kind: 'ok', label: 'Working' }, last: 'Oct 13, 11:02 AM', action: 'Update login' },
  { name: 'MetLife Dental', payers: 'MetLife', usedFor: 'Benefits · Claim status', login: 'billing@alamo…', status: { kind: 'warn', label: 'Needs a new password' }, last: 'Oct 12, 9:30 AM', action: 'Update login' },
  { name: 'Guardian', payers: 'Guardian', usedFor: '—', login: '—', status: { kind: 'mute', label: 'No account yet' }, last: 'Never', action: 'Add a login' },
];

/** Add / Update login dialog (rcm-07 §3). Mock: on save the row shows the
 *  "Working" test state. */
function LoginDialog({
  portal,
  options,
  onClose,
  onSave,
}: {
  /** The portal being updated, or null in Add mode. */
  portal: string | null;
  options: string[];
  onClose: () => void;
  onSave: (portal: string, username: string) => void;
}) {
  const [chosen, setChosen] = useState(portal ?? options[0] ?? '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const canSave = chosen !== '' && username.trim() !== '' && password !== '';
  return (
    <Dialog
      title={portal ? `Update login · ${portal}` : 'Add a login'}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="setd-btn setd-btn-quiet" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="setd-btn setd-btn-primary"
            disabled={!canSave}
            onClick={() => { onSave(chosen, username); }}
          >
            Save and test
          </button>
        </>
      }
    >
      <label className="setd-field">
        <span className="setd-field-label">Portal</span>
        <select
          className="setd-input"
          value={chosen}
          disabled={portal !== null}
          aria-label="Portal"
          onChange={(e) => { setChosen(e.target.value); }}
        >
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
      <TextField label="Username" value={username} onChange={setUsername} autoComplete="off" />
      <TextField label="Password" value={password} onChange={setPassword} type="password" autoComplete="off" />
    </Dialog>
  );
}

function PayerPortalsPage() {
  const [portals, setPortals] = useState(PORTALS);
  const [dialog, setDialog] = useState<{ portal: string | null } | null>(null);
  const [guardianAsked, setGuardianAsked] = useState(false);
  const portalOptions = portals.map((p) => p.name);

  const saveLogin = (portalName: string, username: string) => {
    const masked = username.length > 8 ? `${username.slice(0, 8)}…` : username;
    setPortals((list) =>
      list.map((p) =>
        p.name === portalName
          ? { ...p, login: masked, status: { kind: 'ok', label: 'Working' }, last: 'Just now', action: 'Update login' }
          : p,
      ),
    );
    setDialog(null);
  };

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Where sign-in codes go" />
        <Row
          title="Codes for every portal go to ELVA"
          help="ELVA added its own number and email as the sign-in contact on each account, so it signs in on its own. Your office phone no longer gets portal codes."
          right={
            <>
              <span className="setd-val">(210) 555-0187</span>
              <span className="setd-val mute">codes.alamoheights@elva.ai</span>
            </>
          }
        />
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Portals ELVA signs in to" meta="7 portals · 9 payers" />
        <div style={{ overflowX: 'auto' }}>
          <table className="setd-tbl">
            <thead>
              <tr>
                <th>Portal</th>
                <th>Used for</th>
                <th>Login</th>
                <th>Status</th>
                <th>Last signed in</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {portals.map((p) => (
                <tr key={p.name}>
                  <td>
                    <div className="name">{p.name}</div>
                    <div className="sub">{p.payers}</div>
                  </td>
                  <td style={{ color: 'var(--fg2)' }}>{p.usedFor}</td>
                  <td className="mono" style={{ color: 'var(--mfg)' }}>{p.login}</td>
                  <td>
                    <Pill kind={p.status.kind}>{p.status.label}</Pill>
                  </td>
                  <td style={{ color: 'var(--fg2)' }}>{p.last}</td>
                  <td className="r">
                    <button
                      type="button"
                      className="setd-link"
                      onClick={() => { setDialog({ portal: p.login === '—' ? null : p.name }); }}
                    >
                      {p.action}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="setd-two">
        <section className="setd-card">
          <SectionHeader n="03" title="MetLife needs a new password" />
          <p className="setd-desc">
            MetLife asked for a new password on Oct 14. Change it on MetLife&rsquo;s site, then type the new one here.
            Until then ELVA calls MetLife instead.
          </p>
          <button
            type="button"
            className="setd-btn setd-btn-primary"
            style={{ minHeight: 34 }}
            onClick={() => { setDialog({ portal: 'MetLife Dental' }); }}
          >
            Type the new password
          </button>
        </section>
        <section className="setd-card">
          <SectionHeader n="04" title="Guardian has no account yet" />
          <p className="setd-desc">
            ELVA can open one for the practice. Guardian needs the owner&rsquo;s OK and the practice&rsquo;s tax ID.
          </p>
          {guardianAsked ? (
            <Pill kind="mute">Request sent to Dr. Carter</Pill>
          ) : (
            <button
              type="button"
              className="setd-btn setd-btn-primary"
              style={{ minHeight: 34 }}
              onClick={() => { setGuardianAsked(true); }}
            >
              Ask Dr. Carter to approve
            </button>
          )}
        </section>
      </div>

      {dialog ? (
        <LoginDialog
          portal={dialog.portal}
          options={portalOptions}
          onClose={() => { setDialog(null); }}
          onSave={saveLogin}
        />
      ) : null}

      <div className="setd-quiet">
        <b>How logins are kept.</b> Encrypted, and never shown again after you save them. ELVA uses them only to sign in
        for this practice, and every visit is logged step by step in{' '}
        <Link to="/elva-work" className="setd-link">
          ELVA&rsquo;s work
        </Link>
        .
      </div>
      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-08 Calls ──

function CallsPage() {
  const [callback, setCallback] = useState('(210) 555-0140');
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="The number ELVA calls from" />
        <Row
          title="Calling number"
          help={
            <>
              RCM&rsquo;s own number. Payers see this name: <b>ELVA for Alamo Heights Dental</b>.
            </>
          }
          right={<span className="setd-val">(210) 555-0186</span>}
        />
        <Row
          title="When a payer calls back"
          help={
            <>
              Calls to this number are forwarded to your office, {callback}. ·{' '}
              <button
                type="button"
                className="setd-link"
                onClick={() => { setDraft(callback); setOpen((v) => !v); }}
              >
                {open ? 'Close' : 'Change'}
              </button>
            </>
          }
        >
          {open ? (
            <div className="setd-inline-edit">
              <input
                className="setd-input mono"
                aria-label="Number a payer calls back"
                value={draft}
                onChange={(e) => { setDraft(formatPhone(e.target.value)); }}
              />
              <button
                type="button"
                className="setd-btn setd-btn-primary setd-btn-sm"
                disabled={draft.replace(/\D/g, '').length !== 10}
                onClick={() => { setCallback(draft); setOpen(false); }}
              >
                Save
              </button>
            </div>
          ) : null}
        </Row>
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="What ELVA tells payers" />
        <p className="setd-desc">
          Payers ask for the tax ID, the NPI and the address on every call. ELVA reads them from your practice details.
        </p>
        <Link to="/settings/practice" className="setd-link">
          Practice details for payers
        </Link>
      </section>

      <section className="setd-card">
        <SectionHeader n="03" title="When ELVA calls" />
        <Row
          title="Payers"
          help="During each payer's provider line hours. ELVA learns them from the payer."
          right={<span className="setd-val mute">Automatic</span>}
        />
        <Row
          title="Patients"
          help={`Only patients whose record in ${PMS} allows calls, and only inside these hours. ELVA calls a patient only after texts haven't worked.`}
          right={
            <>
              <Select value="Mon–Fri" options={['Mon–Fri', 'Mon–Sat', 'Every day']} label="Days ELVA may call patients" />
              <Select value="9:00 AM" options={TIMES} label="Call patients from" />
              <Select value="6:00 PM" options={TIMES} label="Call patients until" />
            </>
          }
        />
      </section>

      <section className="setd-card">
        <SectionHeader n="04" title="Recordings" />
        <Row
          title="Keep call recordings for"
          help="With the transcript, in ELVA's work. Recordings are never downloaded."
          right={<Select value="6 years" options={['6 years', '7 years', '10 years']} label="Keep recordings for" />}
        />
      </section>

      <div className="setd-quiet">
        <b>Every call opens the same way:</b> &ldquo;This is ELVA, an automated assistant calling for Alamo Heights
        Dental. This call is recorded.&rdquo;
      </div>
      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-09 Texts & email ──

interface Dentist {
  name: string;
  role: string;
  phone?: string;
  status: { kind: PillKind; label: string; note?: string };
  action: string;
}

const DENTISTS: Dentist[] = [
  { name: 'Dr. Emily Carter', role: 'Owner · General dentist', phone: '(210) 555-0131', status: { kind: 'ok', label: 'Confirmed' }, action: 'Change' },
  { name: 'Dr. Olivia Brooks', role: 'General dentist', phone: '(210) 555-0176', status: { kind: 'ok', label: 'Confirmed' }, action: 'Change' },
  { name: 'Dr. Marcus Lee', role: 'Endodontist', status: { kind: 'warn', label: 'Missing', note: "Clinical appeals for Dr. Lee's patients wait until it's added." }, action: 'Add number' },
];

/** Mobile number dialog (rcm-09 §01). Mock: on save the row turns to
 *  "Waiting for a reply". */
function MobileDialog({
  dentist,
  onClose,
  onSave,
  onRemove,
}: {
  dentist: Dentist;
  onClose: () => void;
  onSave: (phone: string) => void;
  onRemove: () => void;
}) {
  const wasConfirmed = dentist.status.label === 'Confirmed' && dentist.phone;
  const [phone, setPhone] = useState('');
  const first = dentist.name.split(' ').pop() ?? dentist.name;
  return (
    <Dialog
      title={`${dentist.name}'s mobile`}
      help="ELVA will text this number once to confirm it. The number is confirmed when the dentist replies."
      onClose={onClose}
      actions={
        <>
          {dentist.phone ? (
            <button type="button" className="setd-remove-link" onClick={onRemove}>Remove the number</button>
          ) : null}
          <button type="button" className="setd-btn setd-btn-quiet" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="setd-btn setd-btn-primary"
            disabled={phone.replace(/\D/g, '').length !== 10}
            onClick={() => { onSave(phone); }}
          >
            Save
          </button>
        </>
      }
    >
      <PhoneField label="Mobile number" value={phone} onChange={setPhone} />
      {wasConfirmed ? (
        <p className="setd-dialog-help" style={{ margin: '12px 0 0' }}>
          Until the new number is confirmed, ELVA keeps using {dentist.phone}. Dr. {first} will get one confirming text.
        </p>
      ) : null}
    </Dialog>
  );
}

function TextsEmailPage() {
  const [dentists, setDentists] = useState(DENTISTS);
  const [editing, setEditing] = useState<string | null>(null);
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderName, setSenderName] = useState('Alamo Heights Dental billing');
  const [senderLocal, setSenderLocal] = useState('billing');
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [forwardTo, setForwardTo] = useState('office@alamoheightsdental.com');

  const editingDentist = dentists.find((d) => d.name === editing) ?? null;

  const saveMobile = (name: string, phone: string) => {
    setDentists((list) =>
      list.map((d) =>
        d.name === name
          ? { ...d, phone, status: { kind: 'mute', label: 'Waiting for a reply' }, action: 'Change' }
          : d,
      ),
    );
    setEditing(null);
  };
  const removeMobile = (name: string) => {
    setDentists((list) =>
      list.map((d) =>
        d.name === name
          ? { name: d.name, role: d.role, status: { kind: 'warn', label: 'Missing' }, action: 'Add number' }
          : d,
      ),
    );
    setEditing(null);
  };

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Dentists' mobile numbers" />
        <p className="setd-desc">
          ELVA texts dentists when it needs a clinical answer, and to approve the letter in a clinical appeal. A number
          is confirmed when the dentist replies to ELVA&rsquo;s first text.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table className="setd-tbl">
            <thead>
              <tr>
                <th>Dentist</th>
                <th>Mobile</th>
                <th>Status</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {dentists.map((d) => (
                <tr key={d.name}>
                  <td>
                    <div className="name">{d.name}</div>
                    <div className="sub">{d.role}</div>
                  </td>
                  <td className="mono">{d.phone ?? 'Not added'}</td>
                  <td>
                    <Pill kind={d.status.kind}>{d.status.label}</Pill>
                    {d.status.note ? <div className="sub">{d.status.note}</div> : null}
                  </td>
                  <td className="r">
                    <button type="button" className="setd-link" onClick={() => { setEditing(d.name); }}>
                      {d.action}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Texts" />
        <Row
          title="Texting number"
          help="The same number ELVA calls from. Used for dentists, and for patients when ELVA needs an answer about their insurance."
          right={<span className="setd-val">(210) 555-0186</span>}
        />
        <Row
          title="Quiet hours for patients"
          help="In the patient's own time zone."
          right={
            <>
              <span className="setd-val mute">Not before</span>
              <Select value="8:00 AM" options={TIMES} label="Quiet hours start" />
              <span className="setd-val mute">or after</span>
              <Select value="8:00 PM" options={TIMES} label="Quiet hours end" />
            </>
          }
        />
        <p className="setd-desc">
          ELVA texts only patients whose record in {PMS} allows texts. A patient who replies STOP gets no more texts from RCM.
          ELVA handles it on its own.
        </p>
      </section>

      <section className="setd-card">
        <SectionHeader n="03" title="Email" />
        <Row
          title="Sender"
          help="Payers that accept email for appeals, and patients signing a payer's form, see this name and address."
          right={
            <>
              <span className="setd-val">{senderName}</span>
              <span className="setd-val mute">{senderLocal}@alamoheightsdental.com</span>
              <button type="button" className="setd-link" onClick={() => { setSenderOpen((v) => !v); }}>
                {senderOpen ? 'Close' : 'Change'}
              </button>
            </>
          }
        >
          {senderOpen ? (
            <div className="setd-inline-edit">
              <input
                className="setd-input"
                aria-label="Sender name"
                value={senderName}
                onChange={(e) => { setSenderName(e.target.value); }}
              />
              <input
                className="setd-input mono"
                aria-label="Sender address"
                value={senderLocal}
                onChange={(e) => { setSenderLocal(e.target.value.replace(/[@\s]/g, '')); }}
              />
              <span className="setd-val mute">@alamoheightsdental.com</span>
              <button type="button" className="setd-btn setd-btn-primary setd-btn-sm" onClick={() => { setSenderOpen(false); }}>Save</button>
            </div>
          ) : null}
        </Row>
        <Row
          title="Your domain"
          help="Verified, so ELVA's email doesn't land in spam."
          right={<Pill kind="ok">Verified Oct 1</Pill>}
        />
        <Row
          title="Replies go to"
          help="ELVA reads replies first, then forwards anything for the office."
          right={
            <>
              <span className="setd-val mute">ELVA, then the office</span>
              <button type="button" className="setd-link" onClick={() => { setRepliesOpen((v) => !v); }}>
                {repliesOpen ? 'Close' : 'Change'}
              </button>
            </>
          }
        >
          {repliesOpen ? (
            <div className="setd-inline-edit">
              <input
                className="setd-input"
                type="email"
                aria-label="Forward to"
                value={forwardTo}
                onChange={(e) => { setForwardTo(e.target.value); }}
              />
              <button type="button" className="setd-btn setd-btn-primary setd-btn-sm" onClick={() => { setRepliesOpen(false); }}>Save</button>
              <button type="button" className="setd-remove-link" style={{ marginRight: 0 }} onClick={() => { setRepliesOpen(false); }}>Cancel</button>
            </div>
          ) : null}
        </Row>
      </section>

      {editingDentist ? (
        <MobileDialog
          dentist={editingDentist}
          onClose={() => { setEditing(null); }}
          onSave={(phone) => { saveMobile(editingDentist.name, phone); }}
          onRemove={() => { removeMobile(editingDentist.name); }}
        />
      ) : null}

      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-10 Fax & mail ──

function FaxMailPage() {
  const [coverName, setCoverName] = useState('Alamo Heights Dental · Billing');
  const [coverDraft, setCoverDraft] = useState('');
  const [coverOpen, setCoverOpen] = useState(false);
  const [returnAddr, setReturnAddr] = useState('4501 Broadway, Suite 200, San Antonio, TX 78209');
  const [addrDraft, setAddrDraft] = useState('');
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrChecked, setAddrChecked] = useState(true);

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Faxes ELVA sends" />
        <Row
          title="Fax number"
          help={
            <>
              Appeals and payer forms go out from this number. Faxes coming in are set up in{' '}
              <Link to="/settings/paper" className="setd-link">
                Paper and fax
              </Link>
              .
            </>
          }
          right={<span className="setd-val">(210) 555-0189</span>}
        />
        <Row
          title="Name on the cover page"
          right={
            <>
              <span className="setd-val">{coverName}</span>
              <button
                type="button"
                className="setd-link"
                onClick={() => { setCoverDraft(coverName); setCoverOpen((v) => !v); }}
              >
                {coverOpen ? 'Close' : 'Change'}
              </button>
            </>
          }
        >
          {coverOpen ? (
            <div className="setd-inline-edit">
              <input
                className="setd-input"
                aria-label="Name on the cover page"
                value={coverDraft}
                onChange={(e) => { setCoverDraft(e.target.value); }}
              />
              <button type="button" className="setd-btn setd-btn-primary setd-btn-sm" onClick={() => { setCoverName(coverDraft); setCoverOpen(false); }}>Save</button>
            </div>
          ) : null}
        </Row>
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Letters ELVA mails" />
        <div className="setd-two" style={{ gridTemplateColumns: 'minmax(0,1fr) 420px', marginTop: 12 }}>
          <div>
            <Row
              title="Return address"
              help="On every letter and envelope. USPS checked it on Oct 1."
              right={
                <>
                  <Pill kind={addrChecked ? 'ok' : 'warn'}>{addrChecked ? 'Checked' : 'Checking…'}</Pill>
                  <button
                    type="button"
                    className="setd-link"
                    onClick={() => { setAddrDraft(returnAddr); setAddrOpen((v) => !v); }}
                  >
                    {addrOpen ? 'Close' : 'Change'}
                  </button>
                </>
              }
            >
              {addrOpen ? (
                <div className="setd-inline-edit">
                  <input
                    className="setd-input"
                    aria-label="Return address"
                    value={addrDraft}
                    onChange={(e) => { setAddrDraft(e.target.value); }}
                  />
                  <button
                    type="button"
                    className="setd-btn setd-btn-primary setd-btn-sm"
                    onClick={() => { setReturnAddr(addrDraft); setAddrChecked(true); setAddrOpen(false); }}
                  >
                    Save and check with USPS
                  </button>
                </div>
              ) : null}
            </Row>
            <Row
              title="Letterhead"
              help="Practice name, address and phone, from Practice details."
              right={
                <Link to="/settings/practice" className="setd-link">
                  Practice details
                </Link>
              }
            />
            <p className="setd-desc">Postage and fax are included in your ELVA plan.</p>
            <p className="setd-desc">
              Certified mail with a return receipt is the default when a payer takes mail. Signed return receipts come
              back to ELVA electronically. Which way each payer gets appeals is set in{' '}
              <Link to="/settings/appeals" className="setd-link">
                Appeals
              </Link>
              .
            </p>
          </div>
          <div className="setd-preview">
            <div className="setd-preview-name">Alamo Heights Dental</div>
            <div>{returnAddr}</div>
            <div>(210) 555-0140</div>
            <div className="setd-preview-div">Sent for the practice by ELVA</div>
          </div>
        </div>
      </section>

      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-11 Practice details ──

function Fact({ k, v, source, onFlag }: { k: string; v: React.ReactNode; source: string; onFlag?: (field: string) => void }) {
  return (
    <div className="setd-fact">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
      <span className="src">
        {source}
        {onFlag ? (
          <>
            {' '}·{' '}
            <button type="button" className="setd-link" onClick={() => { onFlag(k); }}>
              This isn&rsquo;t right
            </button>
          </>
        ) : null}
      </span>
    </div>
  );
}

const SRC = `From ${PMS}`;

function PracticePage() {
  const [flagField, setFlagField] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [formSent, setFormSent] = useState(false);

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="The practice" />
        <p className="setd-desc">
          Every call, portal form, claim and appeal uses these. ELVA reads them from your PMS; change them there and ELVA
          picks it up the same day.
        </p>
        <Fact k="Billing name" v="Alamo Heights Dental, PLLC" source={SRC} onFlag={setFlagField} />
        <Fact k="Tax ID" v={<span className="m">74-2918830</span>} source={SRC} onFlag={setFlagField} />
        <Fact k="Group NPI" v={<span className="m">1376049882</span>} source={SRC} onFlag={setFlagField} />
        <Fact k="Taxonomy" v={<><span className="m">1223G0001X</span> · General practice</>} source={SRC} onFlag={setFlagField} />
        <Fact k="Billing address" v="4501 Broadway, Suite 200, San Antonio, TX 78209" source={SRC} onFlag={setFlagField} />
        <Fact k="Pay-to address" v="Same as billing" source={SRC} onFlag={setFlagField} />
        <Fact k="Phone" v={<span className="m">(210) 555-0140</span>} source={SRC} onFlag={setFlagField} />
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Dentists" />
        <div style={{ overflowX: 'auto' }}>
          <table className="setd-tbl">
            <thead>
              <tr>
                <th>Dentist</th>
                <th>NPI</th>
                <th>Taxonomy</th>
                <th>Networks</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="name">Dr. Emily Carter</td>
                <td className="mono">1548293017</td>
                <td><span className="mono">1223P0221X</span> · Prosthodontics</td>
                <td style={{ color: 'var(--fg2)' }}><NetworksSummaryLink dentist="Dr. Emily Carter" /></td>
              </tr>
              <tr>
                <td className="name">Dr. Olivia Brooks</td>
                <td className="mono">1659204128</td>
                <td><span className="mono">1223G0001X</span> · General practice</td>
                <td style={{ color: 'var(--fg2)' }}><NetworksSummaryLink dentist="Dr. Olivia Brooks" /></td>
              </tr>
              <tr>
                <td className="name">Dr. Marcus Lee</td>
                <td className="mono">1770315239</td>
                <td><span className="mono">1223E0200X</span> · Endodontics</td>
                <td style={{ color: 'var(--fg2)' }}><NetworksSummaryLink dentist="Dr. Marcus Lee" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <NetworksSection />

      <section className="setd-card setd-mismatch">
        <SectionHeader n="!" title="MetLife has a different pay-to address" />
        <p className="setd-desc">
          MetLife&rsquo;s records show 1200 Austin Hwy, the address before the move. Payments may go to the old address.
          ELVA can send MetLife the change form with Dr. Carter&rsquo;s signature.
        </p>
        {formSent ? (
          <Pill kind="mute">Change form sent to MetLife</Pill>
        ) : (
          <button type="button" className="setd-btn setd-btn-primary" style={{ minHeight: 34 }} onClick={() => { setFormSent(true); }}>
            Send the change form
          </button>
        )}
      </section>

      {flagField ? (
        <Dialog
          title={`Tell us what's wrong with "${flagField}"`}
          help="This goes to the ELVA team. We check it and, if the PMS has it wrong, help you fix it there."
          onClose={() => { setFlagField(null); setNote(''); }}
          actions={
            <>
              <button type="button" className="setd-btn setd-btn-quiet" onClick={() => { setFlagField(null); setNote(''); }}>Cancel</button>
              <button
                type="button"
                className="setd-btn setd-btn-primary"
                disabled={note.trim() === ''}
                onClick={() => { setFlagField(null); setNote(''); }}
              >
                Send to ELVA
              </button>
            </>
          }
        >
          <label className="setd-field">
            <span className="setd-field-label">What&rsquo;s wrong, and how you know</span>
            <textarea
              className="setd-input"
              style={{ height: 96, padding: '10px 14px', resize: 'vertical' }}
              value={note}
              onChange={(e) => { setNote(e.target.value); }}
            />
          </label>
        </Dialog>
      ) : null}
    </>
  );
}

// ── rcm-12 Signatures ──

function SignaturesPage() {
  const [withdrawn, setWithdrawn] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [leeLinkSent, setLeeLinkSent] = useState(false);

  return (
    <>
      {withdrawn ? (
        <div className="setd-strip">
          <b>ELVA isn&rsquo;t sending anything on its own.</b> The authorization was withdrawn on Oct 16.
        </div>
      ) : null}

      <section className="setd-card">
        <SectionHeader n="01" title="Your claims and appeals authorization" />
        {withdrawn ? (
          <Row
            title="Withdrawn by Ava Thompson (Lone Star Billing), Oct 16"
            help="ELVA prepares claims and appeals; a person sends each one. Signed by Dr. Emily Carter on Oct 1, 2026."
            right={
              <>
                <Pill kind="warn">Withdrawn</Pill>
                <button type="button" className="setd-link">View the old one</button>
                <button
                  type="button"
                  className="setd-btn setd-btn-primary setd-btn-sm"
                  onClick={() => { setWithdrawn(false); }}
                >
                  Sign a new one
                </button>
              </>
            }
          />
        ) : (
          <Row
            title="Signed by Dr. Emily Carter, owner"
            help="Oct 1, 2026. Lets ELVA prepare and send claims, corrections and appeals for the practice, and sign billing appeals and payer forms for it. Separate from your HIPAA business associate agreement."
            right={
              <>
                <Pill kind="ok">Signed</Pill>
                <button type="button" className="setd-link">
                  View it
                </button>
                <button
                  type="button"
                  className="setd-link"
                  style={{ color: 'var(--fg2)' }}
                  onClick={() => { setConfirming(true); }}
                >
                  Withdraw
                </button>
              </>
            }
          />
        )}
      </section>

      {confirming ? (
        <Dialog
          title="Withdraw the authorization?"
          help={
            <>
              ELVA will still prepare claims, corrections and appeals, but a person will have to send each one. ELVA
              won&rsquo;t sign billing appeals or payer forms for the practice.
              <br />
              <span style={{ fontSize: 13, color: 'var(--mfg)' }}>
                You can sign a new one at any time. Withdrawing is logged with your name.
              </span>
            </>
          }
          onClose={() => { setConfirming(false); }}
          actions={
            <>
              <button type="button" className="setd-btn setd-btn-quiet" onClick={() => { setConfirming(false); }}>Keep it</button>
              <button
                type="button"
                className="setd-btn setd-btn-danger"
                onClick={() => { setWithdrawn(true); setConfirming(false); }}
              >
                Withdraw
              </button>
            </>
          }
        />
      ) : null}

      <section className="setd-card">
        <SectionHeader n="02" title="Who signs" />
        <Row
          title="Billing appeals and payer forms"
          help="Records, coding and payment mistakes, enrollment and change forms. ELVA signs with this person's signature on file."
          right={
            <Select
              value="Dr. Emily Carter"
              options={['Dr. Emily Carter', 'Dr. Olivia Brooks']}
              label="Who signs billing appeals and payer forms"
            />
          }
        />
        <div style={{ paddingTop: 4 }}>
          {[
            ['Clinical appeals:', 'the treating dentist. ELVA texts them the letter; they read and approve it, then ELVA signs. An edit means a new approval.'],
            ["A patient's appeal:", "the patient signs the payer's form, which ELVA texts them."],
            ["A form that needs a dentist's own signature:", 'that dentist signs on screen.'],
          ].map(([lead, rest]) => (
            <div key={lead} className="setd-lst-row">
              <span className="setd-glyph-ok" aria-hidden>
                ·
              </span>
              <span>
                <b>{lead}</b> <span className="rest">{rest}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="setd-card">
        <SectionHeader n="03" title="Signatures on file" />
        <p className="setd-desc">
          Each dentist draws or uploads a signature once and agrees that ELVA may use it. Every use is logged.
        </p>
        <Row
          title="Dr. Emily Carter"
          help="Adopted and agreed Oct 1, 2026"
          right={
            <>
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 24, color: '#0b3433' }}>
                Carter
              </span>
              <Pill kind="ok">On file</Pill>
              <button type="button" className="setd-link">
                View
              </button>
            </>
          }
        />
        <Row
          title="Dr. Olivia Brooks"
          help="Adopted and agreed Oct 1, 2026"
          right={
            <>
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 24, color: '#0b3433' }}>
                Brooks
              </span>
              <Pill kind="ok">On file</Pill>
              <button type="button" className="setd-link">
                View
              </button>
            </>
          }
        />
        <Row
          title="Dr. Marcus Lee"
          help="Not set up yet. His clinical appeals wait for his signature."
          right={
            leeLinkSent ? (
              <>
                <Pill kind="mute">Link sent Oct 16</Pill>
                <button type="button" className="setd-link" onClick={() => { setLeeLinkSent(true); }}>Send again</button>
              </>
            ) : (
              <>
                <Pill kind="warn">Not set up</Pill>
                <button type="button" className="setd-ghost-btn" style={{ marginTop: 0 }} onClick={() => { setLeeLinkSent(true); }}>
                  Text him a link to sign
                </button>
              </>
            )
          }
        />
      </section>

      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-13 PMS access ──

function PmsPage() {
  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Connection" />
        <Fact k="PMS" v={PMS} source="Set up Sep 3" />
        <Fact k="Last sync" v="Today, 10:42 AM" source="Every few minutes" />
        <Fact k="Full backup" v="Received Sep 3, 2026. Used for Practice Baseline." source="Onboarding" />
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title={`How ELVA writes to ${PMS}`} />
        <p className="setd-desc">
          Where {PMS} has an API for something, ELVA uses it. Where it doesn&rsquo;t, ELVA works {PMS}&rsquo;s screens
          the way a person would, and logs every step as text.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table className="setd-tbl">
            <thead>
              <tr>
                <th>What</th>
                <th>How</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Read charts, claims, payments and documents', `${PMS}'s API`, 'Every few minutes'],
                ['Create or change a claim', `${PMS}'s API`, 'After your team confirms, or on autopilot'],
                ['Submit a claim', `ELVA works ${PMS}'s screens`, `Through ${PMS}'s own clearinghouse`],
                ['Post a payment and its write-offs', `${PMS}'s API`, 'Read back and checked after every posting'],
                ['Write off a balance', `${PMS}'s API`, 'Only after a person decides'],
                ["File a document in the patient's record", `${PMS}'s API`, 'Every document ELVA reads'],
              ].map(([what, how, when]) => (
                <tr key={what}>
                  <td className="name">{what}</td>
                  <td style={{ color: 'var(--fg)' }}>{how}</td>
                  <td style={{ color: 'var(--fg2)' }}>{when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="setd-card">
        <SectionHeader n="03" title="Who sends claims" />
        <p className="setd-desc">
          Your team doesn&rsquo;t send claims from {PMS} any more: ELVA sends every claim after it&rsquo;s confirmed in Claims
          to review. If a claim goes out from {PMS} without ELVA, ELVA finds it on the next sync, shows it in Claims as sent
          by your team, and follows it like any other.
        </p>
      </section>

      <section className="setd-card">
        <SectionHeader n="04" title={`ELVA's user in ${PMS}`} />
        <Row
          title="ELVA RCM"
          help={`ELVA signs in to ${PMS} as its own user, so everything it changes is easy to find in ${PMS}'s history.`}
          right={<Pill kind="ok">Active</Pill>}
        />
      </section>

      <div className="setd-quiet">
        <b>Set up with the ELVA team at onboarding.</b> To change anything here, write to your ELVA contact.
      </div>
    </>
  );
}

// ── rcm-15 Paper and fax ──

interface Sender {
  name: string;
  addr: string;
  last: string;
}

const INITIAL_SENDERS: Sender[] = [
  { name: 'eFax forwarding', addr: 'faxes@alamoheightsdental.com', last: 'Today, 7:18 AM' },
  { name: 'Front desk scanner', addr: 'scanner@alamoheightsdental.com', last: 'Yesterday, 3:02 PM' },
  { name: 'Lone Star Billing', addr: 'docs@lonestarbilling.com', last: 'Oct 12' },
];

function PaperPage() {
  const [senders, setSenders] = useState(INITIAL_SENDERS);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddr, setNewAddr] = useState('');
  const [testSent, setTestSent] = useState(false);

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Faxes coming in" />
        <p className="setd-desc">
          Anything from another sender is turned away, so a stranger can&rsquo;t put a document into your records.
        </p>
        <Row
          title="ELVA's fax address"
          help="Forward the practice's incoming faxes here. ELVA reads each one and sends it where it belongs."
          right={
            <>
              <span className="setd-val">docs.alamoheights@fax.elva.ai</span>
              <Pill kind="ok">Working</Pill>
            </>
          }
        />
        <div className="setd-ap-label" style={{ marginTop: 16 }}>
          Accept faxes from
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="setd-tbl">
            <thead>
              <tr>
                <th>Sender</th>
                <th>Address</th>
                <th>Last received</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {senders.map((s) => (
                <tr key={s.addr}>
                  <td className="name">{s.name}</td>
                  <td className="mono">{s.addr}</td>
                  <td style={{ color: 'var(--fg2)' }}>{s.last}</td>
                  <td className="r">
                    <button
                      type="button"
                      className="setd-link"
                      style={{ color: 'var(--fg2)' }}
                      onClick={() => { setSenders((list) => list.filter((x) => x.addr !== s.addr)); }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button type="button" className="setd-ghost-btn" style={{ marginTop: 0 }} onClick={() => { setAdding(true); }}>
            Add a sender
          </button>
          <button
            type="button"
            className="setd-link"
            style={{ color: 'var(--fg2)' }}
            onClick={() => { setTestSent(true); }}
          >
            Send a test fax
          </button>
          {testSent ? <span className="setd-row-help">Test fax sent — it will show as received in a moment.</span> : null}
        </div>
      </section>

      {adding ? (
        <Dialog
          title="Add a sender"
          help="Faxes from this address are accepted into your records. Everything else is turned away."
          onClose={() => { setAdding(false); setNewName(''); setNewAddr(''); }}
          actions={
            <>
              <button type="button" className="setd-btn setd-btn-quiet" onClick={() => { setAdding(false); setNewName(''); setNewAddr(''); }}>Cancel</button>
              <button
                type="button"
                className="setd-btn setd-btn-primary"
                disabled={newName.trim() === '' || newAddr.trim() === ''}
                onClick={() => {
                  setSenders((list) => [...list, { name: newName, addr: newAddr, last: 'Never' }]);
                  setAdding(false);
                  setNewName('');
                  setNewAddr('');
                }}
              >
                Add
              </button>
            </>
          }
        >
          <TextField label="Sender" value={newName} onChange={setNewName} />
          <TextField label="Address" value={newAddr} onChange={setNewAddr} type="email" mono inputMode="email" />
        </Dialog>
      ) : null}

      <section className="setd-card">
        <SectionHeader n="02" title="Keeping documents" />
        <Row
          title="Keep original documents for"
          help="Matches how long you keep patient records."
          right={<Select value="7 years" options={['1 year', '3 years', '7 years', 'Forever']} label="Keep original documents for" />}
        />
        <p className="setd-desc">
          Documents that aren&rsquo;t for RCM, and duplicates, are listed as Skipped and deleted after 30 days.
        </p>
      </section>

      <div className="setd-quiet">
        <b>Every way in stays on.</b> Uploads, iPad photos, portal downloads and faxes all land in Document Center.
      </div>
      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── rcm-16 How ELVA's work is counted ──

function HowCountedPage() {
  const mins = useCountedMinutes();
  const canEdit = useCanEditSettings();
  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Minutes of staff work per task" />
        <p className="setd-desc">
          Set these to how long each task takes your team. A change counts from that day; past months aren&rsquo;t
          recounted.
        </p>
        <table className="setd-tbl">
          <thead>
            <tr>
              <th>Task</th>
              <th className="r">Counts as</th>
            </tr>
          </thead>
          <tbody>
            {COUNTED_TASKS.map((t) => {
              const changed = mins[t.key] !== t.defaultMin;
              return (
                <tr key={t.key}>
                  <td style={{ color: 'var(--fg)' }}>{t.task}</td>
                  <td className="r">
                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <NumberSetting
                        label={`Minutes for ${t.task.replace(/^An? /, '').toLowerCase()}`}
                        value={mins[t.key]}
                        min={COUNTED_MIN}
                        max={COUNTED_MAX}
                        unit="minutes"
                        suffix=" min"
                        readOnly={!canEdit}
                        onCommit={(n) => {
                          setCountedMinutes(t.key, n);
                        }}
                      />
                      {canEdit && changed ? (
                        <button
                          type="button"
                          className="setd-ed-link"
                          onClick={() => {
                            resetCountedMinutes(t.key);
                          }}
                        >
                          Reset to ELVA&rsquo;s default
                        </button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td style={{ color: 'var(--fg)' }}>A call</td>
              <td className="r mono">Its real length, hold time included</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Money ELVA found" />
        <p className="setd-desc" style={{ marginTop: 12 }}>
          Counted as found when ELVA spots it, and as paid only when the money arrives in the PMS. Contract adjustments
          are never counted.
        </p>
      </section>

      <SettingsChangeFooter id={COUNTED_FOOTER_ID} />
    </>
  );
}

// ── Opportunities (Patients) ──

const OPPORTUNITIES: { name: string; note: string; on: boolean }[] = [
  { name: 'Unused preventive benefits', note: 'Cleanings or exams left in the benefit year.', on: true },
  { name: 'Crown on a watched tooth', note: 'A tooth the dentist flagged to watch.', on: true },
  { name: 'Night guard after bruxism note', note: 'When the chart mentions grinding.', on: true },
  { name: 'Perio maintenance due', note: 'Following active periodontal treatment.', on: true },
  { name: 'Fluoride for high-risk patients', note: 'Where the plan still covers it.', on: false },
];

function OpportunitiesPage() {
  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Procedures to monitor" />
        <p className="setd-desc">
          ELVA watches these on every patient and raises one on their insurance page when the coverage, the benefits left
          or the chart make it worth mentioning. Switch off anything this practice doesn&rsquo;t do.
        </p>
        {OPPORTUNITIES.map((o) => (
          <Row
            key={o.name}
            title={o.name}
            help={o.note}
            right={<LocalSwitch initial={o.on} label={o.name} />}
          />
        ))}
      </section>
      <ChangeLine by={CH.by} org={CH.org} date={CH.date} />
    </>
  );
}

// ── Payer rules (Reference, read-only) ──

function PayersPage() {
  const [ls, patch] = useListUrlState({ q: '', page: '1' });
  const page = Math.max(1, Number(ls.page) || 1);
  const filtered = applySearch(PAYER_RULES, ls.q, (r) => `${r.payer} ${r.rule} ${r.source}`);
  const rows = paginate(filtered, page, 50);
  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="What ELVA knows about your payers" meta={`${String(PAYER_RULE_PAYER_COUNT)} payers · ${filtered.length.toLocaleString()} rules`} />
        <p className="setd-desc">
          Every rule ELVA has learned for the payers you bill, with its source. Read-only — these are learned from payer
          manuals, portals and your own claims, never typed in.
        </p>
        <FilterBar
          search={ls.q}
          onSearch={(v) => { patch({ q: v, page: '1' }); }}
          searchPlaceholder="Search payer, rule or source"
          showDateRange={false}
        />
        {filtered.length === 0 ? (
          <ListEmpty {...(ls.q.trim() !== '' ? { query: ls.q } : {})} message={ls.q.trim() === '' ? 'No payer rules yet.' : undefined} />
        ) : (
          <>
            <table className="setd-tbl">
              <thead>
                <tr>
                  <th>Payer</th>
                  <th>Rule</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="name"><Link to={payerPathFor(r.payer)} className="setd-payerlink">{r.payer}</Link></td>
                    <td style={{ color: 'var(--fg2)' }}>{r.rule}</td>
                    <td className="mono" style={{ color: 'var(--mfg)', fontSize: 12.5 }}>
                      {r.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <NumberedPager page={page} pageSize={50} total={filtered.length} onPage={(p) => { patch({ page: String(p) }); }} />
          </>
        )}
      </section>
    </>
  );
}

/**
 * One payer's rules (/settings/payers/:payerId). The plan goes in the query:
 * ?plan=ppo scrolls to that plan's rules and marks them. An id that isn't in the
 * one payer list shows "We can't find this payer" in place of the rules (D202).
 */
export function PayerDetailPage({ payerId }: { payerId: string }) {
  const payer = findPayer(payerId);
  const [params] = useSearchParams();
  const location = useLocation();
  const planParam = (params.get('plan') ?? '').toLowerCase();

  useEffect(() => {
    if (!payer || planParam === '') return;
    document.getElementById(`plan-${planParam}`)?.scrollIntoView({ block: 'start' });
  }, [payer, planParam]);

  if (!payer) {
    return (
      <section className="setd-card">
        <NotFoundInPage thing="payer" listLabel="payers" listTo="/settings/payers" />
      </section>
    );
  }
  // A looser id (delta-dental) lands on the one canonical id.
  if (payer.id !== payerId) return <Navigate to={`/settings/payers/${payer.id}${location.search}`} replace />;

  const rules = rulesForPayer(payer);
  const everyPlan = rules.filter((r) => r.plan === null);
  const groups = payer.plans.map((pl) => ({ ...pl, rules: rules.filter((r) => r.plan === pl.id) }));

  const table = (rows: typeof rules) => (
    <table className="setd-tbl">
      <thead>
        <tr>
          <th>Rule</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td style={{ color: 'var(--fg2)' }}>{r.rule}</td>
            <td className="mono" style={{ color: 'var(--mfg)', fontSize: 12.5 }}>{r.source}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <p style={{ margin: '0 0 10px' }}>
        <Link to="/settings/payers" className="setd-payerlink">‹ All payers</Link>
      </p>
      <section className="setd-card">
        <SectionHeader n="01" title={payer.name} meta={`${String(rules.length)} rules · ${String(payer.plans.length)} plans`} />
        <p className="setd-desc">
          What ELVA has learned about {payer.name}, with its source. Read-only: these come from payer manuals, portals and
          your own claims, never typed in. Rules for one plan are kept apart from the others.
        </p>
        <h3 className="setd-plan-h">Every {payer.name} plan</h3>
        {everyPlan.length === 0 ? <p className="setd-desc">No rule that applies to every plan yet.</p> : table(everyPlan)}
      </section>
      {groups.map((g) => {
        const on = planParam === g.id;
        return (
          <section
            key={g.id}
            id={`plan-${g.id}`}
            className={`setd-card setd-plan${on ? ' setd-plan-hl' : ''}`}
            aria-label={`${payer.name} ${g.label} rules`}
            {...(on ? { 'aria-current': 'location' as const } : {})}
          >
            <SectionHeader n="" title={`${payer.name} ${g.label}`} meta={`${String(g.rules.length)} rules`} />
            {g.rules.length === 0 ? <p className="setd-desc">No rule specific to this plan yet.</p> : table(g.rules)}
          </section>
        );
      })}
    </>
  );
}

// ── dispatch ──

export function renderSettingsPage(slug: RcmSettingsSlug): React.ReactNode | null {
  switch (slug) {
    case 'portals':
      return <PayerPortalsPage />;
    case 'calls':
      return <CallsPage />;
    case 'texts-email':
      return <TextsEmailPage />;
    case 'fax-mail':
      return <FaxMailPage />;
    case 'practice':
      return <PracticePage />;
    case 'signatures':
      return <SignaturesPage />;
    case 'pms':
      return <PmsPage />;
    case 'paper':
      return <PaperPage />;
    case 'how-counted':
      return <HowCountedPage />;
    case 'opportunities':
      return <OpportunitiesPage />;
    case 'payers':
      return <PayersPage />;
    default:
      return null;
  }
}
